import React, { useState } from "react";
import Select, { ActionMeta, components, MenuPlacement, MultiValue, OptionProps } from "react-select";
import { getIn, useFormikContext } from "formik";
import { useTranslation } from "react-i18next";
import { CustomControlIconError } from "../ReactSelect/CustomControlIconError";
import { CustomDropdownIndicator } from "../ReactSelect/CustomDropdownIndicator";
import multiSelectStyles from "./multiSelectStyles";

export interface MultiOption {
    value: number;
    label: string;
}

interface MultiSelectProps {
    id?: string;
    key?: string;
    name?: string;
    value?: MultiOption[];
    options: MultiOption[];
    placeholder: string;
    onChange: (option: MultiValue<MultiOption> | null, actionMeta: ActionMeta<MultiOption>) => void;
    isSearchable: boolean;
    disabled?: boolean;
    menuPlacement?: MenuPlacement;
    menuPortalTarget?: HTMLElement;
    onKeyDown?: (event: React.KeyboardEvent<HTMLInputElement>) => void;
    hasError?: boolean;
    searchPlaceholder?: string;
    lockValues?: Array<MultiOption["value"]>;
}

const MultiSelect: React.FC<MultiSelectProps> = ({
    id,
    name,
    options,
    value = [],
    onChange,
    placeholder,
    disabled,
    isSearchable,
    menuPlacement = "bottom",
    menuPortalTarget = document.body,
    hasError,
    searchPlaceholder,
    lockValues = [],
}) => {
    const { t } = useTranslation();
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [inputValue, setInputValue] = useState("");

    const formikContext = useFormikContext<any>();

    // If select used in Formik, then we pass it to 'name' and then we can use the useFormikContext
    let errors: any;
    let touched: any;
    let submitCount = 0;

    if (formikContext && name) {
        // `getIn` is used to handle both regular and nested field names
        errors = formikContext?.errors && getIn(formikContext.errors, name);
        touched = formikContext?.touched && getIn(formikContext.touched, name);
        submitCount = formikContext?.submitCount ?? 0;
    }

    if (hasError !== undefined) {
        errors = hasError;
        touched = hasError;
    }

    const showError = Boolean(errors && (touched || submitCount > 0));

    const isLocked = (v: MultiOption["value"]) => lockValues?.includes(v);

    const CustomMultiValueRemove: React.FC<any> = (props) => {
        if (isLocked(props.data.value)) return null;

        return (
            <components.MultiValueRemove {...props}>
                <i className="bi bi-x-lg fs-7" style={{ display: "block", lineHeight: 1, verticalAlign: "middle" }} />
            </components.MultiValueRemove>
        );
    };

    const CheckboxOption: React.FC<OptionProps<MultiOption, true>> = (props) => {
        const { isSelected, isDisabled, label } = props;
        return (
            <components.Option {...props} innerProps={{ ...props.innerProps, className: "checkbox-option" }}>
                <div className="d-flex align-items-center gap-2">
                    <input
                        type="checkbox"
                        checked={isSelected}
                        disabled={isDisabled}
                        readOnly
                        aria-hidden="true"
                        className="form-check-input pointer-event-none m-0"
                    />
                    <span>{label}</span>
                </div>
            </components.Option>
        );
    };

    const CustomPlaceholder = (props: any) => {
        const showSearchPlaceholder = isSearchable && isMenuOpen && inputValue === "";
        return (
            <components.Placeholder {...props}>
                {showSearchPlaceholder ? t(searchPlaceholder) : placeholder}
            </components.Placeholder>
        );
    };

    const handleInputChange = (newValue: string) => {
        setInputValue(newValue || "");
    };

    return (
        <>
            <Select<MultiOption, true>
                id={id}
                inputId={id}
                name={name}
                isMulti
                options={options}
                value={value}
                onChange={onChange}
                placeholder={placeholder}
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                isClearable
                {...{ error: showError }}
                styles={multiSelectStyles(showError, showError)}
                menuPlacement={menuPlacement}
                menuPortalTarget={menuPortalTarget}
                isOptionDisabled={(opt, selected) => isLocked(opt.value) && selected.some((s) => s.value === opt.value)}
                components={{
                    Control: CustomControlIconError,
                    DropdownIndicator: CustomDropdownIndicator,
                    Placeholder: CustomPlaceholder,
                    Option: CheckboxOption,
                    MultiValueRemove: CustomMultiValueRemove,
                }}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                onMenuOpen={() => setIsMenuOpen(true)}
                onMenuClose={() => setIsMenuOpen(false)}
                backspaceRemovesValue={false}
                isDisabled={disabled}
            />
        </>
    );
};

export default MultiSelect;
